import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useModalRoute } from "@/hooks/useModalRoute";

import type { Class, GradeLevel } from "@markeroo/shared";

import {
  useClasses,
  useCreateClass,
  useUpdateClass,
  useDeleteClassForever,
  useGCStatus,
} from "../../api/hooks";
import { useFeatureFlag } from "@/providers/FeatureFlagProvider";
import { PageHeader } from "../../components/PageHeader";
import { Card } from "../../components/cards";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../components/Modal";
import { ClassCard } from "./components/ClassCard";
import { ClassFormModal } from "./components/ClassFormModal";
import { GCCourseImportModal } from "./components/GCCourseImportModal";
import { useNotification } from "../../providers/NotificationProvider";
import {
  cardContainer,
  mutedText,
  pageContainer,
} from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { useEscapeKey } from "@/hooks/useEscapeKey";
import { notifications, ui, confirmations } from "@/strings";
import type { NotificationKind } from "@/providers/NotificationProvider";
import { useSetBreadcrumbs } from "@/hooks/useSetBreadcrumbs";
import { InfoBanner } from "@/components/InfoBanner";
import { safeGetItem, safeSetItem } from "@/utils/safeLocalStorage";
import logoFull from "../../images/logo-full.png";

export function ClassesPage() {
  const navigate = useNavigate();
  const gcIntegrationEnabled = useFeatureFlag("gc_integration");
  const aiDetectorEnabled = useFeatureFlag("ai_detector_extension");
  const { data, isLoading } = useClasses();
  const createMutation = useCreateClass();
  const updateMutation = useUpdateClass();
  const deleteForeverMutation = useDeleteClassForever();
  const gcStatusQuery = useGCStatus({ enabled: gcIntegrationEnabled });
  const { show } = useNotification();
  const TOAST_SUCCESS: NotificationKind = "success";
  const TOAST_ERROR: NotificationKind = "error";

  // All modals are route-driven for deep-linking support
  const {
    isOpen: isCreateModalOpen,
    open: openCreateModal,
    close: closeCreateModal,
  } = useModalRoute({
    pattern: "/classes/new",
    parentPath: "/classes",
  });

  const {
    isOpen: isGCImportModalOpen,
    open: openGCImportModal,
    close: closeGCImportModal,
  } = useModalRoute({
    pattern: "/classes/import",
    parentPath: "/classes",
  });

  const {
    isOpen: isArchiveModalOpen,
    close: closeArchiveModal,
    open: openArchiveModal,
    params: archiveParams,
  } = useModalRoute({
    pattern: "/classes/:classId/archive",
    parentPath: "/classes",
  });

  const {
    isOpen: isDeleteModalOpen,
    close: closeDeleteModal,
    open: openDeleteModal,
    params: deleteParams,
  } = useModalRoute({
    pattern: "/classes/:classId/delete",
    parentPath: "/classes",
  });

  const [showArchived, setShowArchived] = useState(false);
  const [aiDetectorBannerDismissed, setAiDetectorBannerDismissed] = useState(() => {
    const stored = safeGetItem("aiDetectorBannerDismissed");
    return stored === "true";
  });

  // Only show GC mode if feature flag is enabled
  const isGCMode = gcIntegrationEnabled && (gcStatusQuery.data?.gc_mode ?? false);

  // Set breadcrumbs for this page (just the page title, home is automatic)
  useSetBreadcrumbs([{ label: ui.headers.classes }]);

  // Get classes data
  const allClasses = data?.items ?? [];
  const activeClasses = allClasses.filter((cls) => !cls.archived);
  const archivedClasses = allClasses.filter((cls) => cls.archived);

  // Look up classes from route params
  const archiveConfirmClass = archiveParams.classId
    ? allClasses.find((c) => c.id === archiveParams.classId)
    : null;
  const deleteForeverConfirmClass = deleteParams.classId
    ? allClasses.find((c) => c.id === deleteParams.classId)
    : null;

  const handleCreateClass = (data: { name: string; defaultElaGradeLevel: GradeLevel }) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        show(notifications.classes.created, TOAST_SUCCESS);
        closeCreateModal();
      },
      onError: () => {
        show(notifications.classes.createFailed, TOAST_ERROR);
      },
    });
  };

  const handleToggleArchive = (cls: Class) => {
    openArchiveModal({ classId: cls.id });
  };

  const handleConfirmArchive = () => {
    if (!archiveConfirmClass) return;
    updateMutation.mutate(
      {
        id: archiveConfirmClass.id,
        payload: { archived: !archiveConfirmClass.archived },
      },
      {
        onSuccess: () => {
          show(archiveConfirmClass.archived ? notifications.classes.unarchived : notifications.classes.archived, TOAST_SUCCESS);
          closeArchiveModal();
        },
        onError: () => {
          show(notifications.classes.updateFailed, TOAST_ERROR);
          closeArchiveModal();
        },
      }
    );
  };

  const handleCancelArchive = () => {
    closeArchiveModal();
  };

  const handleDeleteForever = (cls: Class) => {
    openDeleteModal({ classId: cls.id });
  };

  const handleConfirmDeleteForever = () => {
    if (!deleteForeverConfirmClass) return;
    deleteForeverMutation.mutate(deleteForeverConfirmClass.id, {
      onSuccess: () => {
        show(notifications.classes.permanentlyDeleted, TOAST_SUCCESS);
        closeDeleteModal();
      },
      onError: () => {
        show(notifications.classes.deleteFailed, TOAST_ERROR);
        closeDeleteModal();
      },
    });
  };

  const handleCancelDeleteForever = () => {
    closeDeleteModal();
  };

  const handleDismissAiDetectorBanner = () => {
    setAiDetectorBannerDismissed(true);
    safeSetItem("aiDetectorBannerDismissed", "true");
  };

  const handleOpenCreateModal = () => {
    createMutation.reset();
    openCreateModal();
  };

  const handleCloseCreateModal = () => {
    createMutation.reset();
    closeCreateModal();
  };

  useEscapeKey(isCreateModalOpen, handleCloseCreateModal);

  return (
    <div className={pageContainer}>
        <PageHeader>
          <PageHeader.Title>{ui.headers.classes}</PageHeader.Title>
          <PageHeader.Actions>
            {isGCMode ? (
              <button
                type="button"
                onClick={() => openGCImportModal()}
                className={buttonVariants.primary}
                data-testid="import-gc-courses-button"
              >
                {ui.googleClassroom.importFromGoogleClassroom}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleOpenCreateModal}
                className={buttonVariants.primary}
                data-testid="create-class-button"
              >
                {ui.actions.create} {ui.labels.class}
              </button>
            )}
          </PageHeader.Actions>
        </PageHeader>

        {aiDetectorEnabled && !aiDetectorBannerDismissed && (
          <InfoBanner
            title={ui.aiDetector.featureDiscoveryTitle}
            infoLines={[ui.aiDetector.featureDiscoveryDescription]}
            onDismiss={handleDismissAiDetectorBanner}
            action={
              <Link
                to="/ai-detection"
                className={buttonVariants.primary}
              >
                {ui.aiDetector.featureDiscoveryLearnMore}
              </Link>
            }
          />
        )}

        <section className={cardContainer}>

        {isLoading && (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <div className="h-6 w-48 animate-pulse rounded bg-border" />
              </Card>
            ))}
          </div>
        )}

        {!isLoading && activeClasses.length === 0 && !showArchived && (
          <div className="flex flex-col items-center rounded-xl border border-border bg-surface-secondary px-8 py-12 text-center">
            <img
              src={logoFull}
              alt="Markeroo mascot"
              className="mb-6 h-32 w-auto"
            />
            <h2 className="mb-2 text-xl font-semibold text-text-theme-primary">
              {ui.empty.noClassesTitle}
            </h2>
            <p className="mb-6 max-w-md text-text-theme-secondary">
              {ui.empty.noClassesSubtitle}
            </p>
            {isGCMode ? (
              <button
                type="button"
                onClick={() => openGCImportModal()}
                className={buttonVariants.primary}
                data-testid="empty-state-import-gc-button"
              >
                {ui.googleClassroom.importFromGoogleClassroom}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleOpenCreateModal}
                className={buttonVariants.primary}
                data-testid="empty-state-create-class-button"
              >
                {ui.empty.noClassesCreateButton}
              </button>
            )}
          </div>
        )}

        {!isLoading && activeClasses.length > 0 && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {activeClasses.map((cls: Class) => (
              <ClassCard
                key={cls.id}
                classData={cls}
                onNavigate={(c) => void navigate(`/classes/${c.id}`)}
                onArchive={handleToggleArchive}
                onDeleteForever={handleDeleteForever}
                isArchiving={updateMutation.isPending && archiveConfirmClass?.id === cls.id}
                isDeleting={deleteForeverMutation.isPending && deleteForeverConfirmClass?.id === cls.id}
              />
            ))}
          </div>
        )}
      </section>

      {archivedClasses.length > 0 && !showArchived && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => setShowArchived(true)}
            className={buttonVariants.link}
          >
            {ui.actions.showArchived}
          </button>
        </div>
      )}

      {showArchived && archivedClasses.length > 0 && (
        <>
          <div className="my-6 border-t border-border" />
          <h3 className="mb-4 text-lg font-semibold text-text-theme-secondary">{ui.headers.archivedClasses || `${ui.headers.archivedAssignments.replace('assignments','classes')}`}</h3>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 opacity-60">
            {archivedClasses.map((cls: Class) => (
              <ClassCard
                key={cls.id}
                classData={cls}
                onNavigate={(c) => void navigate(`/classes/${c.id}`)}
                onArchive={handleToggleArchive}
                onDeleteForever={handleDeleteForever}
                isArchiving={updateMutation.isPending && archiveConfirmClass?.id === cls.id}
                isDeleting={deleteForeverMutation.isPending && deleteForeverConfirmClass?.id === cls.id}
              />
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => setShowArchived(false)}
              className={buttonVariants.link}
            >
              {ui.actions.hideArchived}
            </button>
          </div>
        </>
      )}

      <ClassFormModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSubmit={handleCreateClass}
        isSubmitting={createMutation.isPending}
      />

      <Modal isOpen={isArchiveModalOpen} onClose={handleCancelArchive} size="sm" aria-label={`${ui.actions.confirm} ${ui.actions.archive} ${ui.labels.class}`}>
        <ModalHeader title={archiveConfirmClass?.archived ? `${ui.actions.unarchive} ${ui.labels.class}` : `${ui.actions.archive} ${ui.labels.class}`} onClose={handleCancelArchive} />
        <ModalBody>
          <p className={mutedText}>
            {archiveConfirmClass?.archived
              ? confirmations.classes.unarchive(archiveConfirmClass.name)
              : confirmations.classes.archive(archiveConfirmClass?.name || "")}
          </p>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className={buttonVariants.secondary}
            onClick={handleCancelArchive}
          >
            {ui.actions.cancel}
          </button>
          <button
            className={archiveConfirmClass?.archived ? buttonVariants.primary : buttonVariants.danger}
            type="button"
            onClick={handleConfirmArchive}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? ui.actions.editing : archiveConfirmClass?.archived ? ui.actions.unarchive : ui.actions.archive}
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={isDeleteModalOpen} onClose={handleCancelDeleteForever} size="sm" aria-label={`${ui.actions.confirm} ${ui.actions.deleteForever}`}>
        <ModalHeader title={`${ui.actions.deleteForever} ${ui.labels.class}`} onClose={handleCancelDeleteForever} />
        <ModalBody>
          <p className={mutedText}>
            {deleteForeverConfirmClass ? confirmations.classes.delete(deleteForeverConfirmClass.name) : null}
          </p>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className={buttonVariants.secondary}
            onClick={handleCancelDeleteForever}
          >
            {ui.actions.cancel}
          </button>
          <button
            className={buttonVariants.danger}
            type="button"
            onClick={handleConfirmDeleteForever}
            disabled={deleteForeverMutation.isPending}
          >
            {deleteForeverMutation.isPending ? ui.actions.deleting : ui.actions.deleteForever}
          </button>
        </ModalFooter>
      </Modal>

      <GCCourseImportModal
        isOpen={isGCImportModalOpen}
        onClose={closeGCImportModal}
      />
    </div>
  );
}
